import { de } from '../../../lib/i18n/de';
import { MoneyInput } from '../../../components/finance/MoneyInput';
import { FieldHelp } from '../../../components/forms/FieldHelp';
import { effectiveBurnRateCents, type ResourcesDraft } from '../../../lib/store/wizard';

interface LiquiditaetStepProps {
  resources: ResourcesDraft;
  onLiquidityChange: (cents: number | null) => void;
  onBurnRateChange: (cents: number | null) => void;
  onLiabilitiesChange: (cents: number | null) => void;
}

/** Sub-Step 4.3: Liquidität & Reichweite (liquide Mittel, Burn-Rate, Monate). */
export function LiquiditaetStep({
  resources,
  onLiquidityChange,
  onBurnRateChange,
  onLiabilitiesChange,
}: LiquiditaetStepProps) {
  const t = de.steps.resources.subSteps.liquidity;
  const burn = effectiveBurnRateCents(resources);

  // Netto-Liquidität = liquide Mittel − kurzfristige Verbindlichkeiten.
  const netLiquidityCents =
    resources.liquidityCents !== null
      ? resources.liquidityCents - (resources.shortTermLiabilitiesCents ?? 0)
      : null;
  const runwayMonths =
    netLiquidityCents !== null && burn !== null && burn > 0 ? netLiquidityCents / burn : null;

  return (
    <div className="space-y-8">
      <p className="max-w-prose font-serif text-base italic text-stone-500 md:text-lg">{t.intro}</p>

      <div>
        <MoneyInput
          valueCents={resources.liquidityCents}
          onChange={onLiquidityChange}
          label={t.assetsLabel}
          hint={t.assetsHint}
          placeholder="z.B. 6.000,00"
        />
        <div className="mt-2">
          <FieldHelp pathKey="resources.liquidity.assets" />
        </div>
      </div>

      <MoneyInput
        valueCents={resources.shortTermLiabilitiesCents}
        onChange={onLiabilitiesChange}
        label={t.liabilitiesLabel}
        hint={t.liabilitiesHint}
        placeholder="z.B. 1.200,00"
      />

      <MoneyInput
        valueCents={resources.burnRateCents ?? resources.costsCents}
        onChange={onBurnRateChange}
        label={t.burnLabel}
        hint={t.burnHint}
        placeholder="z.B. 1.800,00"
      />

      <div className="border-t border-stone-200 pt-6">
        <div className="block">
          <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            {t.runwayLabel}
          </span>
          <p className="mt-1 max-w-prose font-serif text-sm italic text-stone-500">
            {t.runwayHint}
          </p>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="font-serif text-3xl font-black tabular-nums tracking-tight text-stone-900 md:text-4xl">
              {runwayMonths === null
                ? '—'
                : runwayMonths.toLocaleString('de-DE', { maximumFractionDigits: 1 })}
            </span>
            {runwayMonths !== null && (
              <span aria-hidden="true" className="font-serif text-xl text-stone-500">
                Monate
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
